/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Assumption as BaseAssumption,
  Forecast
} from '@futrli-sdk/predict';
import {
  WorkspaceMember,
  WorkspaceMemberOrganisation as WorkspaceMemberOrganisationSDK,
  WorkspaceMemberRole,
  WorkspaceMemberDataSource,
  WorkspaceMemberName
} from '@futrli-sdk/people';
import { WrappedComponentProps } from '@futrli/localisation';
import { AnyObject, LoadingRequestState, Omit } from '@futrli/shared-services';
import { AuthStoreState as AuthStoreStateFromAuth } from '@futrli/auth';

import { VAT_PERIOD } from './constants';

type InjectedIntl = WrappedComponentProps['intl'];

export interface NormalisedData<T> {
  [id: string]: T;
}

export type RadioClick = React.MouseEvent<HTMLButtonElement, MouseEvent> & { target: { name: string; value: string } };

export interface SelectOption {
  value: string;
  label: string;
}

export interface QueryParams {
  [key: string]: string;
}

export interface NodeInfo {
  name: string;
  type: string;
  static_id?: string;
  organisation_id?: string;
  assumption_type?: 'BasicAssumption' | 'RepeatingAssumption' | 'AiAssumption' | 'UnitAssumption' | 'FormulaAssumption';
  system_assumption_type?: 'unpaid-invoices' | 'vat-payments' | 'year-end';
  assumption_id?: string;
  excluded_from_report?: boolean;
  unused_account?: boolean;
  is_derived?: boolean;
  primary_account_name?: string;
  code?: string;
  extra_display_params?: {
    invoices_or_bills?: string;
  };
  system_account_role?: string | null;
  imported_from_advisor?: boolean;
  period_to_date: string;
}

export interface NodeInfoWithRef extends NodeInfo {
  ref: string;
}

export interface ConsolidatedNodeStructure {
  ref: string;
  children: ConsolidatedNodeStructure[] | null;
}

export interface ConsolidatedFlatNode {
  level: number;
  ref: string;
  parentRef: string | null;
  childrenRefs: string[];
  name: string;
  type: string;
  static_id?: string;
  organisation_id?: string;
  row: number[];
}

export interface ConsolidatedReportDataInfo {
  row: string[];
  is_unused?: boolean;
  period_to_date: string | null;
}

export interface ConsolidatedReportChartItem {
  ref: 'v2:static:bank_accounts:cumulative';
  actuals_start_index: number;
  forecast_start_index: number;
  forecast_end_index: number;
}

export interface ConsolidatedReportData {
  by_ref: Record<string, ConsolidatedReportDataInfo>;
  columns: string[];
  cash_flow_columns: string[];
  chart_items: Record<string, ConsolidatedReportChartItem>;
}

export interface ConsolidatedNodeInfo {
  by_ref: Record<string, NodeInfo>;
}

export interface ConsolidatedForecastReportStructure {
  profit_loss: ConsolidatedNodeStructure[];
  cash_flow: ConsolidatedNodeStructure[];
  balance_sheet: ConsolidatedNodeStructure[];
}
export interface ConsolidatedForecastReportStructureWithTabs extends ConsolidatedForecastReportStructure {
  tabs: ConsolidatedForecastReportTab[];
}

export interface ConsolidatedForecastReportTab {
  children: ConsolidatedForecastReportTabChild[];
  id?: string;
  label?: string;
  tab_style: 'select-one';
}

export interface ConsolidatedForecastReportTabChild {
  find_in: string;
  include_all_children: boolean;
  override_static_id?: string;
  ref: string;
}

export interface ConsolidatedForecastReport {
  data: ConsolidatedReportData;
  node_info: ConsolidatedNodeInfo;
  structure: ConsolidatedForecastReportStructure;
}

export interface AssumptionData {
  date: string;
  amount: string;
}

export type ChartDataId =
  | 'past'
  | 'future'
  | 'futureNegative'
  | 'futureIncome'
  | 'futureCostOfSales'
  | 'futureExpenses';

export interface PredictSettings {
  accounts_payable_account: Account;
  accounts_receivable_account: Account;
  bank_account: Account;
  vat_disabled: boolean;
  vat_account: Account | null;
  vat_period_days: number | null;
  vat_period: VAT_PERIOD | null;
  vat_period_end_month: number | null;
  vat_rate: number | null;
  vat_scheme: string | null;
}

export interface Account {
  id: string;
  name: string;
}

export interface Accounts {
  accounts_payable_account: Account[];
  accounts_receivable_account: Account[];
  bank_account: Account[];
  vat_account: Account[];
}

export type Page = 'next' | 'prev' | 'all';

// @todo: remove when SDK updated
export interface AiAssumption {
  id: string;
  created: string;
  modified: string;
  account_id: string;
  name: string | null;
}

export interface Assumption extends Omit<BaseAssumption, 'ai_assumption'> {
  ai_assumption?: AiAssumption;
}

export interface DataCheckResponse {
  msg: 'Financial data is unavailable' | 'Financial data is available';
}

export interface AnyAction {
  type: string;
  [key: string]: any;
}

export type PredictionsAccountCategories =
  | 'income'
  | 'cost_of_sales'
  | 'expenses'
  | 'other_income'
  | 'other_expenses'
  | 'fixed_assets'
  | 'current_assets'
  | 'non_current_assets'
  | 'current_liabilities'
  | 'non_current_liabilities';

export enum FINANCIAL_INTEGRATION {
  xero = 'xero',
  qbo = 'qbo',
  sage50 = 'sage50'
}

export type MessageFormatter = InjectedIntl['formatMessage'];

export interface Message {
  id: string;
  defaultMessage: string;
  description: string;
}

export interface WorkspaceMemberOrganisationPatch extends Partial<Omit<WorkspaceMemberOrganisationSDK, 'datasources'>> {
  datasources?: Partial<WorkspaceMemberDataSource>[];
}
export interface WorkspaceMemberPatch extends Partial<Omit<WorkspaceMember, 'role' | 'organisations'>> {
  role?: Partial<WorkspaceMemberRole>;
  organisations?: WorkspaceMemberOrganisationPatch[];
}

export interface WorkspaceMemberRolePatch {
  role: Partial<WorkspaceMemberRole>;
}

export interface PartialNormalisedOrg extends Partial<Omit<WorkspaceMemberOrganisationSDK, 'datasources'>> {
  datasources?: NormalisedData<Partial<WorkspaceMemberDataSource>>;
}

export type PartialNormalisedOrgs = NormalisedData<PartialNormalisedOrg>;

export interface WorkspaceMemberInvitation extends Partial<Omit<WorkspaceMember, 'role' | 'organisations'>> {
  email_address: string;
  name: WorkspaceMemberName;
  role: Partial<WorkspaceMemberRole>;
  organisations: PartialNormalisedOrgs;
}
export type WorkspaceInvitation = NormalisedData<WorkspaceMemberInvitation>;

export interface StoreState {
  normalised: NormalisedData<unknown>;
  loadingRequests: LoadingRequestState;
  intlReducer: AnyObject;
}

export interface AuthStoreState extends StoreState {
  authReducers: AuthStoreStateFromAuth;
}

export enum VIEW_TYPES {
  ACCRUALS_PL = 'accruals_pl',
  ACCRUALS_BS = 'accruals_bs',
  CASH_FLOW = 'cashflow'
}

export enum LIABILITIES_CATEGORIES {
  CURRENT_LIABILITIES = 'current_liabilities',
  NON_CURRENT_LIABILITIES = 'non_current_liabilities'
}

export enum ASSETS_CATEGORIES {
  FIXED_ASSETS = 'fixed_assets',
  CURRENT_ASSETS = 'current_assets',
  NON_CURRENT_ASSETS = 'non_current_assets'
}

export enum OTHER_PL_CATEGORIES {
  OTHER_INCOME = 'other_income',
  OTHER_EXPENSES = 'other_expenses'
}

export type PredictionsSubCategories = LIABILITIES_CATEGORIES | ASSETS_CATEGORIES | OTHER_PL_CATEGORIES;

export interface ForecastReplacePayload extends Omit<Forecast, 'kind'> {
  kind: string;
  categories_with_ai_enabled?: string[] | null;
}
